/**
 * The DOM half of the online overlay: `OverlayView` in, a focus-trapped
 * `@bn-games/ui-shell` modal out. Kept out of `OnlineDriver` so the driver — and
 * its unit tests — never touch the DOM; the driver only knows the
 * `OnlineOverlaySurface` interface this satisfies.
 *
 * **The rematch confirmation** (the plan's open question #3, decided at T14).
 * A rematch always starts a brand-new match: the server re-deals and
 * `MatchLayer.reset()` mints a fresh `MatchState`, so accepting one mid-match
 * discards the running scores, the round, and the dealer. Nothing on the wire
 * can soften that, so the guard is here, in front of *sending*: while a
 * Classical match is still in progress, both proposing and accepting go through
 * a confirm dialog first. A finished match — the case the overlay's own
 * game-over state describes — has nothing left to lose and asks nothing.
 */

import {
  confirmDialog,
  createGameStateOverlay,
  type GameStateOverlayButtonId,
  type GameStateOverlayHandle,
  type GameStateOverlaySnapshot,
} from '@bn-games/ui-shell';
import type { OverlayView } from '../network/overlayState.js';
import type { OnlineOverlaySurface } from '../scenes/onlineTransport.js';
import { t } from '../i18n/I18n.js';
import { buildOnlineOverlayLabels } from './onlineOverlayLabels.js';

export interface OnlineOverlayActions {
  requestRematch(): void;
  acceptRematch(): void;
  declineRematch(): void;
  /** Dismiss a state the player may simply walk away from. */
  dismiss(): void;
  /** Leave the table — back to the menu. */
  leave(): void;
  /** True while a Classical match is still running; gates the confirm. */
  isMatchInProgress(): boolean;
}

/** No seat is named — see `onlineOverlayLabels.ts`. */
const NO_OPPONENT_NAME = '';

function snapshotOf(
  state: Exclude<OverlayView['overlay'], 'none'>,
  view: OverlayView,
): GameStateOverlaySnapshot {
  return {
    state,
    opponentName: NO_OPPONENT_NAME,
    gracePeriodEndsAt: view.gracePeriodEndsAt,
    ...(view.gameOverResult !== null ? { gameOverResult: view.gameOverResult } : {}),
  };
}

export function createOnlineOverlayHost(actions: OnlineOverlayActions): OnlineOverlaySurface {
  let handle: GameStateOverlayHandle | null = null;

  /** Ask first when there is a live match to lose; otherwise just send. */
  function guarded(send: () => void): void {
    if (!actions.isMatchInProgress()) {
      send();
      return;
    }
    void confirmDialog({
      title: t('online.rematchConfirm.title'),
      message: t('online.rematchConfirm.message'),
      confirmLabel: t('online.rematchConfirm.confirm'),
      cancelLabel: t('online.rematchConfirm.cancel'),
    }).then((confirmed) => {
      if (confirmed) send();
    });
  }

  function onAction(id: GameStateOverlayButtonId): void {
    switch (id) {
      case 'rematch':
        guarded(() => actions.requestRematch());
        return;
      case 'accept':
        guarded(() => actions.acceptRematch());
        return;
      case 'decline':
        actions.declineRematch();
        return;
      case 'cancel':
        actions.dismiss();
        return;
      case 'leave':
      case 'return-to-menu':
        actions.leave();
        return;
    }
  }

  function close(): void {
    handle?.close();
    handle = null;
  }

  return {
    update: (view) => {
      const state = view.overlay;
      if (state === 'none') {
        close();
        return;
      }
      // Labels as a getter: the shared component re-resolves them on every
      // update, so a language change lands without rebuilding the handle.
      const active = handle ?? createGameStateOverlay(buildOnlineOverlayLabels, { onAction });
      handle = active;
      active.update(snapshotOf(state, view));
    },
    dispose: close,
  };
}
